import React from 'react';
import { Box, DatePicker, Input, InputGroup, parseDate, Portal } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuCalendar } from 'react-icons/lu';
import { getInputStyles } from '@/components/ui/inputStyles.ts';
import { datePickerPopupStyles } from '@/components/common/access-time-selector/styles.ts';
import { formatDisplayDate, parseDisplayDate, toIsoDate } from '@/utils/dateInputUtils.ts';
import { COMPOSER_BAR_CONTROL_H, COMPOSER_PORTAL_CLICK_BOUNDARY_PROPS } from './composerConstants.ts';

// Snug to the DD/MM/YYYY value (the field's widest content). A value-tracking width would jump as
// the date is picked, so it's sized to the format instead.
const DUE_DATE_FIELD_WIDTH = '136px';

interface ComposerDueDateFieldProps {
	/** ISO yyyy-mm-dd, or '' when unset. */
	startDate: string;
	onStartDateChange: (value: string) => void;
	disabled?: boolean;
	highlightPlaceholder?: boolean;
}

/**
 * The task due-date picker, sized to sit in the composer action bar next to the repeat toggle:
 * compact, label-less (the placeholder carries the meaning), with the calendar leading. Opens
 * upward since it lives at the bottom of the screen.
 */
const ComposerDueDateField: React.FC<ComposerDueDateFieldProps> = (props) => {
	const { startDate, onStartDateChange, disabled, highlightPlaceholder = false } = props;
	const dateValue = startDate ? [parseDate(startDate)] : [];

	return (
		<Box flexShrink={0} w={DUE_DATE_FIELD_WIDTH}>
			<DatePicker.Root
				value={dateValue}
				onValueChange={(details) => {
					const selected = details.value[0];
					onStartDateChange(selected ? toIsoDate(selected.year, selected.month, selected.day) : '');
				}}
				openOnClick
				closeOnSelect
				disabled={disabled}
				positioning={{ placement: 'top-start' }}
				format={(date) => formatDisplayDate(date.year, date.month, date.day)}
				parse={parseDisplayDate}
			>
				<DatePicker.Control>
					<InputGroup
						startElementProps={{ px: 2.5 }}
						startElement={
							<DatePicker.Trigger asChild>
								<IconButton
									aria-label="Open date picker"
									variant="ghost"
									size="xs"
									color="brand.solid"
									boxSize={6}
									minW={6}
								>
									<LuCalendar />
								</IconButton>
							</DatePicker.Trigger>
						}
					>
						<DatePicker.Input asChild>
							<Input
								placeholder="Due"
								disabled={disabled}
								w="full"
								{...getInputStyles()}
								_placeholder={{ color: highlightPlaceholder ? 'brand.fg' : 'fg.subtle' }}
								h={COMPOSER_BAR_CONTROL_H}
								textStyle="body.sm"
								boxShadow="xs"
							/>
						</DatePicker.Input>
					</InputGroup>
				</DatePicker.Control>

				<Portal>
					<DatePicker.Positioner {...COMPOSER_PORTAL_CLICK_BOUNDARY_PROPS}>
						<DatePicker.Content {...datePickerPopupStyles}>
							<DatePicker.View view="day">
								<DatePicker.Header />
								<DatePicker.DayTable />
							</DatePicker.View>
							<DatePicker.View view="month">
								<DatePicker.Header />
								<DatePicker.MonthTable />
							</DatePicker.View>
							<DatePicker.View view="year">
								<DatePicker.Header />
								<DatePicker.YearTable />
							</DatePicker.View>
						</DatePicker.Content>
					</DatePicker.Positioner>
				</Portal>
			</DatePicker.Root>
		</Box>
	);
};

export default ComposerDueDateField;
